import {
    FieldMergeFunction,
    FieldPolicy,
    FieldReadFunction,
    TypePolicies
} from '@apollo/client';
import { SafeReadonly } from '@apollo/client/cache/core/types/common';

// Types
import {
    ExperiencesType,
    GetChatMessagesQuery,
    GetChatsQuery,
    GetNotificationsQuery,
    Maybe,
    PaginationMeta,
    TalentCertificatesType,
    TalentEducationsType,
    TalentItSkillsType,
    TalentLanguagesType
} from '~graphqlResources';



const readFromField = (
    fieldName: string
): FieldReadFunction<string | number, string> => (_, { readField }) =>
    readField(fieldName);

const extendOptionFields = {
    fields: {
        label: readFromField('name'),
        value: readFromField('id')
    }
};

const concatPaginatedFieldPolicy: FieldPolicy<
    TalentEducationsType &
        ExperiencesType &
        TalentLanguagesType &
        TalentCertificatesType &
        TalentItSkillsType
> = {
    keyArgs: false,
    merge(existing, incoming) {
        return {
            ...incoming,
            items:
                existing?.items && incoming?.meta?.currentPage !== 1
                    ? [...existing?.items, ...incoming?.items]
                    : incoming?.items,
            meta: incoming?.meta || existing?.meta
        };
    }
};

type PaginatedType<T = any> = {
    items?: Maybe<Array<T>>;
    meta: PaginationMeta;
};

const pseudoCursorPaginationForChatsMerge = <T extends PaginatedType>(
    existing: SafeReadonly<T | undefined>,
    incoming: SafeReadonly<T>
): ReturnType<FieldMergeFunction<T>> => {
    // Handle accidentally overflowing current page which was set by subscribeToMore function
    if (incoming.meta.currentPage > incoming.meta.totalPages)
        return {
            ...existing,
            meta: {
                ...incoming.meta,
                currentPage: incoming.meta.totalPages
            }
        };

    // XXX: This pagination partially depends on subscribeToMore realization
    if (existing?.meta.currentPage === incoming.meta.currentPage) {
        if (
            incoming.meta.itemCount > incoming.meta.itemsPerPage &&
            incoming.meta.itemCount % incoming.meta.itemsPerPage === 0
        ) {
            return {
                ...incoming,
                meta: {
                    ...incoming.meta,
                    itemCount: incoming.meta.itemsPerPage,
                    currentPage: incoming.meta.currentPage + 1
                }
            };
        }

        return incoming;
    }

    return {
        ...incoming,
        items:
            existing?.items && incoming?.meta?.currentPage !== 1
                ? [
                    ...existing?.items,
                    ...incoming?.items.slice(
                        existing?.meta.itemCount - incoming.meta.itemsPerPage
                    )
                ]
                : incoming?.items,
        meta: incoming?.meta || existing?.meta
    };
};

const concatPaginatedChatMessagesFieldPolicy: FieldPolicy<
    GetChatMessagesQuery['getListOfMessages']
> = {
    keyArgs: ['chatId'],
    merge: pseudoCursorPaginationForChatsMerge
};

const concatPaginatedChatsFieldPolicy: FieldPolicy<
    GetChatsQuery['getListOfChats']
> = {
    keyArgs: false,
    merge: pseudoCursorPaginationForChatsMerge
};

const concatPaginatedNotificationsFieldPolicy: FieldPolicy<
    GetNotificationsQuery['getNotifications']
> = {
    keyArgs: false,
    merge: pseudoCursorPaginationForChatsMerge
};

export const typePolicies: TypePolicies = {
    UniversityCourseType: extendOptionFields,
    UniversityDegreeType: extendOptionFields,
    UniversityType: extendOptionFields,
    ExpertiseType: extendOptionFields,
    SkillType: extendOptionFields,
    ItSkillLevelType: extendOptionFields,
    ItSkillType: extendOptionFields,
    CertificateType: extendOptionFields,
    LanguageType: extendOptionFields,
    LanguageLevelType: extendOptionFields,
    AssessmentQuestionType: extendOptionFields,
    // Experience
    EmployerType: extendOptionFields,
    EmployerIndustryType: extendOptionFields,
    EmployerLabelType: extendOptionFields,
    EmployerSizeType: extendOptionFields,
    EmployerTypeType: extendOptionFields,
    NameWithIdResponse: { ...extendOptionFields, keyFields: ['id', 'name'] },
    CampusType: extendOptionFields,
    CvEntity: { ...extendOptionFields, keyFields: ['id', 'name'] },
    Query: {
        fields: {
            getEnterpriseNoteByTalent: concatPaginatedFieldPolicy,
            getTalentEducations: concatPaginatedFieldPolicy,
            getExperiences: concatPaginatedFieldPolicy,
            getTalentItSkills: concatPaginatedFieldPolicy,
            getTalentCertificates: concatPaginatedFieldPolicy,
            getTalentLanguages: concatPaginatedFieldPolicy,
            getListOfMessages: concatPaginatedChatMessagesFieldPolicy,
            getListOfChats: concatPaginatedChatsFieldPolicy,
            getNotifications: concatPaginatedNotificationsFieldPolicy
        }
    }
};

export default typePolicies;
